import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdminButton, AdminCard, AdminErrorBanner, AdminInfoRow, AdminLoadingState, AdminPageLayout, AdminStatusBadge } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import {
  fetchAdminLifeSignalDetail,
  fetchAdminLocationDetail,
  fetchDashboardSummary,
  fetchElderlyUserDetail,
  fetchElderlyUserDevices,
  fetchElderlyUserEmergencyEvents,
  fetchElderlyUserFamilyLinks,
  fetchSeniorHealthHomeSummary,
  fetchSeniorConnectionEvents,
} from "@/lib/api";
import type {
  AdminDeviceConnectionEvent,
  AdminLifeSignalDetail,
  AdminLocationDetail,
  AdminHealthHomeSummary,
  ElderlyUserDetail,
  ElderlyUserDeviceItem,
  ElderlyUserEmergencyEventItem,
  ElderlyUserFamilyLinkItem,
} from "@/types.ts";

type SeniorDetailTab = "overview" | "care" | "location" | "sound" | "connections";

const tabs: Array<{ key: SeniorDetailTab; label: string; icon: string }> = [
  { key: "overview", label: "요약", icon: "ri-dashboard-line" },
  { key: "care", label: "생활·케어", icon: "ri-heart-pulse-line" },
  { key: "location", label: "위치", icon: "ri-map-pin-line" },
  { key: "sound", label: "과거 소리 v2", icon: "ri-sound-module-line" },
  { key: "connections", label: "가족·기기", icon: "ri-links-line" },
];

const SeniorDetailPage = () => {
  const navigate = useNavigate();
  const { seniorUserAccountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedSeniorAccountId = Number(seniorUserAccountId);
  const requestedTab = searchParams.get("tab");
  const initialTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab as SeniorDetailTab : "overview";
  const [activeTab, setActiveTab] = useState<SeniorDetailTab>(initialTab);
  const [profile, setProfile] = useState<ElderlyUserDetail | null>(null);
  const [devices, setDevices] = useState<ElderlyUserDeviceItem[]>([]);
  const [familyLinks, setFamilyLinks] = useState<ElderlyUserFamilyLinkItem[]>([]);
  const [emergencyEvents, setEmergencyEvents] = useState<ElderlyUserEmergencyEventItem[]>([]);
  const [lifeSignal, setLifeSignal] = useState<AdminLifeSignalDetail | null>(null);
  const [location, setLocation] = useState<AdminLocationDetail | null>(null);
  const [healthSummary, setHealthSummary] = useState<AdminHealthHomeSummary | null>(null);
  const [connectionEvents, setConnectionEvents] = useState<AdminDeviceConnectionEvent[]>([]);
  const [businessDate, setBusinessDate] = useState<string | null>(null);
  const [lifeSignalError, setLifeSignalError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [connectionEventsError, setConnectionEventsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadSenior = useCallback(async () => {
    if (!Number.isFinite(parsedSeniorAccountId)) {
      setErrorMessage("Senior 계정 ID가 올바르지 않습니다.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setLifeSignalError(null);
    setLocationError(null);
    setHealthError(null);
    setConnectionEventsError(null);
    try {
      const dashboard = await fetchDashboardSummary();
      const [nextProfile, nextDevices, nextFamilyLinks, nextEmergencyEvents, nextLifeSignal, nextLocation, nextHealth, nextConnectionEvents] =
        await Promise.all([
          fetchElderlyUserDetail(parsedSeniorAccountId),
          fetchElderlyUserDevices(parsedSeniorAccountId),
          fetchElderlyUserFamilyLinks(parsedSeniorAccountId),
          fetchElderlyUserEmergencyEvents(parsedSeniorAccountId),
          fetchAdminLifeSignalDetail(parsedSeniorAccountId)
            .then((data) => ({ data, error: null }))
            .catch((error: unknown) => ({ data: null, error: error instanceof Error ? error.message : "생활신호 조회 실패" })),
          fetchAdminLocationDetail(parsedSeniorAccountId)
            .then((data) => ({ data, error: null }))
            .catch((error: unknown) => ({ data: null, error: error instanceof Error ? error.message : "위치 조회 실패" })),
          fetchSeniorHealthHomeSummary(parsedSeniorAccountId, dashboard.businessDate)
            .then((data) => ({ data, error: null }))
            .catch((error: unknown) => ({ data: null, error: error instanceof Error ? error.message : "Health 조회 실패" })),
          fetchSeniorConnectionEvents(parsedSeniorAccountId)
            .then((data) => ({ data, error: null }))
            .catch((error: unknown) => ({ data: [], error: error instanceof Error ? error.message : "연결 이력 조회 실패" })),
        ]);

      setProfile(nextProfile);
      setDevices(nextDevices);
      setFamilyLinks(nextFamilyLinks);
      setEmergencyEvents(nextEmergencyEvents);
      setLifeSignal(nextLifeSignal.data);
      setLifeSignalError(nextLifeSignal.error);
      setLocation(nextLocation.data);
      setLocationError(nextLocation.error);
      setHealthSummary(nextHealth.data);
      setHealthError(nextHealth.error);
      setConnectionEvents(nextConnectionEvents.data);
      setConnectionEventsError(nextConnectionEvents.error);
      setBusinessDate(dashboard.businessDate);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Senior 통합 상세 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [parsedSeniorAccountId]);

  useEffect(() => {
    void loadSenior();
  }, [loadSenior]);

  useEffect(() => {
    const nextTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab as SeniorDetailTab : "overview";
    setActiveTab(nextTab);
  }, [requestedTab]);

  const selectTab = (tab: SeniorDetailTab) => {
    setActiveTab(tab);
    setSearchParams(tab === "overview" ? {} : { tab }, { replace: true });
  };

  const issueCount = useMemo(() => {
    const openEmergencyCount = emergencyEvents.filter((event) => event.eventStatus !== "RESOLVED").length;
    const lifeSignalIssueCount = lifeSignal?.timeline.filter((item) => item.severity === "danger" || item.riskLevel === "HIGH").length ?? 0;
    return openEmergencyCount + lifeSignalIssueCount;
  }, [emergencyEvents, lifeSignal?.timeline]);

  const latestEmergency = emergencyEvents[0] ?? null;

  return (
    <AdminPageLayout
      title="Senior 통합 상세"
      subtitle="프로필·Keeper·기기·긴급·생활신호·위치·과거 소리 v2 통합 조회"
      hasIssue={issueCount > 0}
      issueLabel={`확인 필요 ${issueCount}건`}
      actions={
        <>
          <AdminButton type="button" variant="secondary" size="sm" onClick={() => navigate("/admin/users")} icon="ri-arrow-left-line">
            목록
          </AdminButton>
          <AdminButton type="button" variant="primary" size="sm" onClick={loadSenior} disabled={loading} icon="ri-refresh-line">
            새로고침
          </AdminButton>
        </>
      }
    >
      <AdminErrorBanner message={errorMessage} />

      {loading ? (
        <AdminLoadingState message="Senior 통합 상세를 불러오는 중입니다." />
      ) : (
        <div className="space-y-4">
          <AdminCard className="overflow-hidden">
            <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-50 text-blue-600">
                  <i className="ri-user-heart-line text-2xl" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900">{profile?.name ?? `Senior #${parsedSeniorAccountId}`}</h2>
                    <AdminStatusBadge label={`ID ${parsedSeniorAccountId}`} tone={issueCount > 0 ? "danger" : "success"} />
                  </div>
                  <p className="mt-1 text-sm text-slate-500">Senior Account 기준 통합 운영 상세</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">기기</p>
                  <p className="mt-1 font-bold text-slate-900">{devices.length}대</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Keeper</p>
                  <p className="mt-1 font-bold text-slate-900">{familyLinks.length}명</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">긴급</p>
                  <p className="mt-1 font-bold text-slate-900">{emergencyEvents.length}건</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">과거 소리 v2</p>
                  <p className="mt-1 font-bold text-slate-900">연결 대기</p>
                </div>
              </div>
            </div>
          </AdminCard>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {tabs.map((tab) => (
              <AdminButton variant="plain" size="auto"
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                className={`flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === tab.key ? "border-blue-200 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </AdminButton>
            ))}
          </div>

          {activeTab === "overview" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <AdminCard className="p-4">
                <h3 className="mb-3 text-sm font-bold text-slate-800">기본 프로필</h3>
                <div className="space-y-1">
                  <AdminInfoRow dense bordered label="이름" value={profile?.name ?? "-"} />
                  <AdminInfoRow dense bordered label="계정 ID" value={profile?.userAccountId ?? "-"} />
                  <AdminInfoRow dense bordered label="생년월일" value={profile?.birthDate ?? "-"} />
                  <AdminInfoRow dense bordered label="성별" value={profile?.gender ?? "-"} />
                  <AdminInfoRow dense bordered label="주소" value={profile?.address ?? "-"} />
                </div>
              </AdminCard>
              <AdminCard className="p-4">
                <h3 className="mb-3 text-sm font-bold text-slate-800">최근 운영 신호</h3>
                <div className="space-y-1">
                  <AdminInfoRow dense bordered label="최근 긴급" value={latestEmergency ? `${latestEmergency.eventStatus} · ${formatDateTime(latestEmergency.occurredAt)}` : "-"} />
                  <AdminInfoRow dense bordered label="Health 요약" value="API 업무일자 계약 대기" />
                  <AdminInfoRow dense bordered label="최근 소리" value="Senior 식별 필터 계약 대기" />
                  <AdminInfoRow dense bordered label="최근 위치" value={locationError ? "조회 실패" : location?.latestLocation ? formatDateTime(location.latestLocation.collectedAt) : "-"} />
                </div>
              </AdminCard>
              <AdminCard className="p-4">
                <h3 className="mb-3 text-sm font-bold text-slate-800">빠른 이동</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Link className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700" to={`/admin/users?userAccountId=${parsedSeniorAccountId}`}>
                    계정 관리
                  </Link>
                  <Link className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" to={`/admin/emergency?seniorUserAccountId=${parsedSeniorAccountId}`}>
                    긴급 관제
                  </Link>
                  <Link className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" to={`/admin/users/seniors/${parsedSeniorAccountId}?tab=care`}>
                    생활신호
                  </Link>
                  <Link className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700" to={`/admin/location/${parsedSeniorAccountId}`}>
                    위치 관제
                  </Link>
                  <Link className="rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700" to="/admin/sound-analysis">
                    과거 소리 v2
                  </Link>
                  <Link className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" to={`/admin/daily-schedules?seniorUserAccountId=${parsedSeniorAccountId}`}>
                    생활 일정
                  </Link>
                </div>
              </AdminCard>
            </div>
          )}

          {activeTab === "care" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
              <AdminCard className="p-4">
                <div className="mb-3 flex items-center justify-between gap-2"><h3 className="text-sm font-bold text-slate-800">Health Connect 생활 데이터</h3><AdminStatusBadge label={healthSummary?.freshnessStatus ?? (healthError ? "조회 실패" : "데이터 없음")} tone={healthError ? "danger" : healthSummary ? "success" : "neutral"} /></div>
                {healthError ? <AdminErrorBanner message={`Health 정보를 불러오지 못했습니다: ${healthError}`} /> : healthSummary ? (
                  <div className="space-y-1">
                    <AdminInfoRow dense bordered label="업무일자" value={businessDate ?? healthSummary.date} />
                    <AdminInfoRow dense bordered label="연결" value={`${healthSummary.connectionStatus} / ${healthSummary.permissionStatus}`} />
                    <AdminInfoRow dense bordered label="걸음" value={healthSummary.steps ?? "-"} />
                    <AdminInfoRow dense bordered label="운동(분)" value={healthSummary.exerciseMinutes ?? "-"} />
                    <AdminInfoRow dense bordered label="거리(m)" value={healthSummary.distanceMeter ?? "-"} />
                    <AdminInfoRow dense bordered label="칼로리(kcal)" value={healthSummary.caloriesKcal ?? "-"} />
                    <AdminInfoRow dense bordered label="수면(분)" value={healthSummary.sleepMinutes ?? "-"} />
                    <AdminInfoRow dense bordered label="마지막 동기화" value={formatDateTime(healthSummary.lastSyncedAtUtc)} />
                  </div>
                ) : <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">Health 데이터가 없습니다.</div>}
              </AdminCard>
              <AdminCard className="p-4">
                <h3 className="mb-3 text-sm font-bold text-slate-800">생활·케어 타임라인</h3>
                {lifeSignalError && <AdminErrorBanner message={`생활신호를 불러오지 못했습니다: ${lifeSignalError}`} />}
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {(lifeSignal?.timeline ?? []).slice(0, 6).map((item, index) => (
                    <div key={`${item.signalType}-${item.recordedAt}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-bold text-slate-700">{item.signalType}</p>
                      <p className="mt-1 text-xs text-slate-500">{item.summary}</p>
                      <p className="mt-2 text-xs text-slate-400">{formatDateTime(item.recordedAt)}</p>
                    </div>
                  ))}
                  {!lifeSignalError && (lifeSignal?.timeline.length ?? 0) === 0 && <p className="text-sm text-slate-500">생활신호 타임라인이 없습니다.</p>}
                </div>
              </AdminCard>
              <AdminCard className="p-4 xl:col-span-2">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-slate-800">긴급 이벤트</h3>
                  <Link className="text-xs font-semibold text-red-600 hover:text-red-700" to={`/admin/emergency?seniorUserAccountId=${parsedSeniorAccountId}`}>
                    Senior 긴급 관제 열기
                  </Link>
                </div>
                <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                  {emergencyEvents.map((event) => (
                    <Link key={event.eventId} to={`/admin/emergency?seniorUserAccountId=${parsedSeniorAccountId}&selectedEvent=${event.eventId}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-red-200 hover:bg-red-50/40">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-700">{event.eventType}</span>
                        <AdminStatusBadge label={event.eventStatus} tone={event.eventStatus === "RESOLVED" ? "success" : "danger"} />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{event.description ?? event.riskLevel}</p>
                      <p className="mt-2 text-xs text-slate-400">{formatDateTime(event.occurredAt)}</p>
                    </Link>
                  ))}
                  {emergencyEvents.length === 0 && <p className="text-sm text-slate-500">긴급 이벤트가 없습니다.</p>}
                </div>
              </AdminCard>
            </div>
          )}

          {activeTab === "location" && (
            <AdminCard className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-slate-800">최신 위치·이력 요약</h3>
                <Link className="text-xs font-semibold text-blue-600 hover:text-blue-700" to={`/admin/location/${parsedSeniorAccountId}`}>
                  위치 상세 열기
                </Link>
              </div>
              {locationError && <AdminErrorBanner message={`위치 정보를 불러오지 못했습니다: ${locationError}`} />}
              <div className="space-y-1">
                <AdminInfoRow dense bordered label="최신 위치" value={location?.latestLocation?.addressDisplayLabel ?? location?.latestLocation?.addressText ?? "-"} />
                <AdminInfoRow dense bordered label="수집 시각" value={location?.latestLocation ? formatDateTime(location.latestLocation.collectedAt) : "-"} />
                <AdminInfoRow dense bordered label="이력" value={`${location?.history.length ?? 0}건`} />
                <AdminInfoRow dense bordered label="중요 장소" value={`${location?.importantPlaces.length ?? 0}건`} />
              </div>
              {!locationError && location && (
                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <h4 className="mb-2 text-xs font-bold text-slate-600">최근 위치 이력</h4>
                    <div className="space-y-2">
                      {location.history.slice(0, 3).map((log) => (
                        <div key={log.locationLogId} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                          <p className="text-xs font-semibold text-slate-700">{log.addressDisplayLabel ?? log.addressText ?? `${log.latitude}, ${log.longitude}`}</p>
                          <p className="mt-1 text-xs text-slate-400">{formatDateTime(log.collectedAt)}</p>
                        </div>
                      ))}
                      {location.history.length === 0 && <p className="text-sm text-slate-500">위치 이력이 없습니다.</p>}
                    </div>
                  </div>
                  <div>
                    <h4 className="mb-2 text-xs font-bold text-slate-600">중요 장소</h4>
                    <div className="space-y-2">
                      {location.importantPlaces.slice(0, 3).map((place) => (
                        <div key={place.importantPlaceId} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                          <p className="text-xs font-semibold text-slate-700">{place.placeName}</p>
                          <p className="mt-1 text-xs text-slate-400">{place.addressText ?? `${place.latitude}, ${place.longitude}`} · 반경 {place.radiusMeter}m</p>
                        </div>
                      ))}
                      {location.importantPlaces.length === 0 && <p className="text-sm text-slate-500">등록된 중요 장소가 없습니다.</p>}
                    </div>
                  </div>
                </div>
              )}
            </AdminCard>
          )}

          {activeTab === "sound" && (
            <AdminCard className="p-4">
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-bold text-slate-800">과거 소리 v2 이벤트</h3>
                <Link className="text-xs font-semibold text-violet-600 hover:text-violet-700" to="/admin/sound-analysis">
                  과거 소리 v2 이력 열기
                </Link>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-800">
                현행 관리자 소리 API는 `subjectKey`만 필터링하며 Senior 계정 ID와 subjectKey의 공식 매핑을 응답하지 않습니다. 빈 목록으로 표시하거나 ID를 추측하지 않고 계약 보완 전까지 Senior별 조회를 중단합니다.
              </div>
            </AdminCard>
          )}

          {activeTab === "connections" && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <AdminCard className="p-4">
                <h3 className="mb-3 text-sm font-bold text-slate-800">기기</h3>
                <div className="space-y-2">
                  {devices.map((device) => (
                    <Link key={device.deviceId} to={`/admin/devices?seniorUserAccountId=${parsedSeniorAccountId}&deviceId=${device.deviceId}`} className="block rounded-2xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-blue-200 hover:bg-blue-50/40">
                      <p className="text-sm font-semibold text-slate-700">{device.deviceName ?? device.deviceUuid}</p>
                      <p className="mt-1 text-xs text-slate-500">{device.status} · {formatDateTime(device.lastOnlineAt)}</p>
                      <p className="mt-1 text-xs text-blue-600">기기 상세·상태 로그 보기</p>
                    </Link>
                  ))}
                  {devices.length === 0 && <p className="text-sm text-slate-500">연결된 기기가 없습니다.</p>}
                </div>
              </AdminCard>
              <AdminCard className="p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-slate-800">Keeper 연결</h3>
                  <Link className="text-xs font-semibold text-teal-600 hover:text-teal-700" to={`/admin/family-connections/${parsedSeniorAccountId}`}>
                    연결 이력·관리 열기
                  </Link>
                </div>
                <div className="space-y-2">
                  {familyLinks.map((link) => (
                    <Link key={link.connectionId} to={`/admin/family-connections/${parsedSeniorAccountId}`} className="block rounded-2xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-teal-200 hover:bg-teal-50/40">
                      <p className="text-sm font-semibold text-slate-700">{link.keeperName}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {link.relationshipLabel ?? "관계 미지정"} · {link.connectionStatus === "ACTIVE" ? "연결 중" : "연결 해제"}
                      </p>
                      <p className="mt-1 text-xs text-teal-600">연결 상세 보기</p>
                    </Link>
                  ))}
                  {familyLinks.length === 0 && <p className="text-sm text-slate-500">연결된 Keeper가 없습니다.</p>}
                </div>
              </AdminCard>
              <AdminCard className="p-4 lg:col-span-2">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-slate-800">최근 기기 연결 원본 이력</h3>
                  <span className="text-xs text-slate-500">최근 7일 · 최대 50건</span>
                </div>
                {connectionEventsError ? <AdminErrorBanner message={`기기 연결 이력을 불러오지 못했습니다: ${connectionEventsError}`} /> : (
                  <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                    {connectionEvents.map((event) => (
                      <Link key={event.connectionEventId} to={`/admin/devices?seniorUserAccountId=${parsedSeniorAccountId}&deviceId=${event.deviceId}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-blue-200 hover:bg-blue-50/40">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold text-slate-700">{event.eventType}</span>
                          <AdminStatusBadge label={event.currentState} tone={event.currentState === "ONLINE" ? "success" : "danger"} />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{event.previousState} → {event.currentState}</p>
                        <p className="mt-1 text-xs text-slate-400">발생 {formatDateTime(event.occurredAt)} · 감지 {formatDateTime(event.detectedAt)}</p>
                        {event.reason && <p className="mt-1 text-xs text-slate-500">{event.reason}</p>}
                      </Link>
                    ))}
                    {connectionEvents.length === 0 && <p className="text-sm text-slate-500">최근 7일 연결 이력이 없습니다.</p>}
                  </div>
                )}
              </AdminCard>
            </div>
          )}
        </div>
      )}
    </AdminPageLayout>
  );
};

export default SeniorDetailPage;
